// src/utils/vehicleCsvFields.ts

import dayjs from 'dayjs';

export type CsvField = {
  key: string;
  label: string;
  extractor: (vehicle: any) => string;
  enabled: boolean;
};

const renderProcessingStatus = (step: string | undefined | null) => {
  if (!step) return "Not Complete";

  if (step === "COMPLETE") return "COMPLETE";

  const match = step.match(/asset(?:Processing|Syndication)_(.+?)_serviceFlag/);
  if (match && match[1]) {
    return match[1].toUpperCase();
  }

  return "Not Complete";
};

export const vehicleCsvFields: CsvField[] = [
  // ✅ Enabled fields (original order preserved)
  {
    key: 'vin',
    label: 'VIN',
    extractor: v => v.assetIdentifier || '',
    enabled: true
  },
  {
    key: 'postDate',
    label: 'Post Date',
    extractor: v => dayjs(Number(v.sk) * 1000).format('YYYY-MM-DD') || '',
    enabled: true
  },
  {
    key: 'bwImageCount',
    label: 'BW Image Count',
    extractor: v => String(v.imageCounts?.exteriorRaw || 0),
    enabled: true
  },
  {
    key: 'croppedImageCount',
    label: 'Cropped Image Count',
    extractor: v => String(v.imageCounts?.exteriorCrop || 0),
    enabled: true
  },
  {
    key: 'croppedProcessStatus',
    label: 'Cropped Process Status',
    extractor: v => v.assetProcessing_crop_processFlag_status || '',
    enabled: true
  },
  {
    key: 'featuredImageCount',
    label: 'Featured Image Count',
    extractor: v => String(v.imageCounts?.exteriorFeatured || 0),
    enabled: true
  },
  {
    key: 'featuredProcessStatus',
    label: 'Featured Process Status',
    extractor: v => v.assetProcessing_featured_processFlag_status || '',
    enabled: true
  },
  {
    key: 'panoImageCount',
    label: 'Pano Image Count',
    extractor: v => String(v.imageCounts?.panoRaw || 0),
    enabled: true
  },
  {
    key: 'syndicationStatus',
    label: 'Syndication Status',
    extractor: v => renderProcessingStatus(v.flowControl_assetSyndication_currentStep) || '',
    enabled: true
  },
  {
    key: 'processingStatus',
    label: 'Processing Status',
    extractor: v => renderProcessingStatus(v.flowControl_assetProcessing_currentStep) || '',
    enabled: true
  },

  // 🔠 Disabled fields (alphabetized by label)
  {
    key: 'ratio',
    label: 'Aspect Ratio',
    extractor: v => v.ratio || '',
    enabled: false
  },
  {
    key: 'backgroundReplacedImageCount',
    label: 'Background Replaced Image Count',
    extractor: v => String(v.imageCounts?.exteriorBgr || 0),
    enabled: false
  },
  {
    key: 'backgroundReplacedProcessStatus',
    label: 'Background Replaced Process Status',
    extractor: v => v.assetProcessing_bgrExterior_processFlag_status || '',
    enabled: false
  },
  {
    key: 'cameraDirection',
    label: 'Camera Direction',
    extractor: v => v.cameraDirection || '',
    enabled: false
  },
  {
    key: 'clientIdentifier',
    label: 'Client Identifier',
    extractor: v => v.clientIdentifier || '',
    enabled: false
  },
  {
    key: 'configurationName',
    label: 'Configuration Name',
    extractor: v => v.configurationName || '',
    enabled: false
  },
  {
    key: 'manualImageCount',
    label: 'Manual Image Count',
    extractor: v => String(v.imageCounts?.manualRaw || 0),
    enabled: false
  },
  {
    key: 'maskImageCount',
    label: 'Mask Image Count',
    extractor: v => String(v.imageCounts?.exteriorMask || 0),
    enabled: false
  },
  {
    key: 'maskProcessStatus',
    label: 'Masking Process Status',
    extractor: v => v.assetProcessing_masking_processFlag_status || '',
    enabled: false
  },
  {
    key: 'undercarriageRawCount',
    label: 'Undercarriage Image Count',
    extractor: v => String(v.imageCounts?.undercarriageRaw || 0),
    enabled: false
  }
];
